// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';

const SHOTS = {
  en: ['/screenshot-english.png', '/shop-en.png', '/order-dashboard-en.png'],
  fr: ['/screenshot-french.png', '/shop-fr.png', '/order-dashboard-fr.png'],
};
const LABELS = {
  en: ['Overview', 'Online shop', 'Order dashboard'],
  fr: ['Vue d’ensemble', 'Boutique en ligne', 'Tableau des commandes'],
};
const INTERVAL = 4500;

export default function Hero({ isFr, setView, scrollToSection }) {
  const shots = isFr ? SHOTS.fr : SHOTS.en;
  const labels = isFr ? LABELS.fr : LABELS.en;
  const [index, setIndex] = useState(0);
  const frameRef = useRef(null);
  const pausedRef = useRef(false);

  // Auto-advance the hero screenshots. Stops for reduced-motion, when the frame
  // is off-screen or the tab is hidden, and while the visitor hovers/focuses it.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let onScreen = true;
    const io = new IntersectionObserver(
      ([entry]) => { onScreen = entry.isIntersecting; },
      { threshold: 0.25 }
    );
    if (frameRef.current) io.observe(frameRef.current);

    const id = setInterval(() => {
      if (onScreen && document.visibilityState === 'visible' && !pausedRef.current) {
        setIndex((n) => (n + 1) % shots.length);
      }
    }, INTERVAL);

    return () => { clearInterval(id); io.disconnect(); };
  }, [shots.length]);

  // Keep the active shot in sync when the language switches the image set.
  useEffect(() => { setIndex(0); }, [isFr]);

  return (
    <section className="dbk-hero">
      <div className="dbk-wrap dbk-hero__grid">
        <div className="dbk-hero__head">
          <h1 className="dbk-h1 dbk-hero__title">
            {isFr
              ? 'Gérez toute votre micro-boulangerie depuis un seul outil.'
              : 'Run your whole micro-bakery from one place.'}
          </h1>
          <div className="dbk-hero__copy">
          <p className="dbk-lede dbk-hero__lede">
            {isFr
              ? 'Recettes, planification de production, ingrédients, commandes, traçabilité, étiquettes et boutique en ligne — sans tableurs.'
              : 'Recipes, production planning, ingredients, orders, traceability, labels and online sales — without spreadsheets.'}
          </p>

          <div className="dbk-hero__cta">
            <button
              type="button"
              className="dbk-btn dbk-btn--primary"
              onClick={() => setView('signup-baker')}
            >
              {isFr ? 'Commencer l’essai gratuit' : 'Start free trial'}
              <ArrowRight size={18} strokeWidth={2} />
            </button>
            <button
              type="button"
              className="dbk-btn dbk-btn--ghost"
              onClick={() => scrollToSection('pricing')}
            >
              {isFr ? 'Voir le tarif' : 'See pricing'}
            </button>
          </div>

          <p className="dbk-fine">
            {isFr
              ? '30 jours d’essai gratuit · Sans carte bancaire · Environ 5 minutes pour tout mettre en place.'
              : '30-day free trial · No credit card · About 5 minutes to set up.'}
          </p>
          </div>
        </div>

        <div
          className="dbk-hero__shot"
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
          onFocusCapture={() => { pausedRef.current = true; }}
          onBlurCapture={() => { pausedRef.current = false; }}
        >
          <div className="dbk-frame dbk-shots" ref={frameRef}>
            {shots.map((src, n) => (
              <img
                key={src}
                src={src}
                alt={`Digi-Bakery — ${labels[n]}`}
                className={n === index ? 'is-active' : ''}
                loading={n === 0 ? 'eager' : 'lazy'}
              />
            ))}
          </div>
          <div className="dbk-shots__dots" role="group" aria-label={isFr ? 'Captures d’écran' : 'Screenshots'}>
            {shots.map((src, n) => (
              <button
                key={src}
                type="button"
                className={n === index ? 'is-active' : ''}
                aria-label={labels[n]}
                aria-current={n === index ? 'true' : undefined}
                onClick={() => setIndex(n)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
